/**
 * <TabBarButton> — a single bottom-navigation tab (Buttons family, L0.1).
 *
 * TOUTE la cellule presse — l'icône ET le libellé. Elle composait auparavant un `<IconButton>`,
 * qui portait seul la pression : sous le doigt, le libellé était une grande cible morte juste
 * sous la cible vivante, ce qui rate exactement là où on vise.
 *
 * L'onglet actif est **souligné** : un trait court sous l'icône, dans la couleur de l'univers.
 * La couleur seule ne suffisait pas à le désigner sur un fond blanc.
 *
 * `badgeCount` pose un compteur sur le glyphe (l'onglet « Messages » de l'élève : ses messages non
 * lus seulement, les lives imminents ont quitté l'onglet). C'est la chip du DS, comme sur le bouton
 * d'aide du header ; rien par défaut.
 */
import React from "react"
import { Platform, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../Badge"
import { FocusRing, focusRingHost } from "../../FocusRing"
import { Icon, type IconName } from "../../icons/Icon"
import { textInkOnFill, type SemanticColorKey } from "../../../theme/tokens/colors"
import { cappedCount } from "../../../utils/cappedCount"
import { pressLink } from "../../../utils/webLinkPress"
import { Text } from "../../typography/Text"

export type TabBarButtonProps = {
    iconName: IconName
    label: string
    active: boolean
    onPress: () => void
    /** Color when active — defaults to `role`, la couleur de l'univers courant. */
    activeColor?: SemanticColorKey
    /** Color when inactive — defaults to `text-muted`. */
    inactiveColor?: SemanticColorKey
    /** Compteur posé sur le glyphe. Absent ou nul : rien. Au-delà de 99, « 99+ ». */
    badgeCount?: number
    /**
     * Sur le web, l'onglet est un vrai lien vers sa page : Ctrl ou Cmd-clic, le clic du milieu et
     * « Copier le lien » sont ceux du navigateur (`utils/webLinkPress`). Absent, ou sur le natif :
     * un bouton, rien de plus.
     */
    href?: string
    testID?: string
}

/** Cible tactile minimale (WCAG 2.2 AA) — la cellule ne descend jamais sous 44 px. */
const MIN_TARGET = 44

/**
 * Le trait de l'onglet actif : sa longueur, son épaisseur, et l'air qu'il garde sous le glyphe.
 *
 * Comme la maquette : un trait franc, **détaché** de l'icône et posé juste au-dessus du libellé.
 * À 2 px il se collait au glyphe et se lisait comme une barre de l'icône ; à 16 × 2 px il ne se
 * voyait plus du tout sur un grand écran. La cellule ouvre son interligne (`gap "3"`) pour lui
 * laisser sa place sans écraser le mot.
 */
const UNDERLINE = { width: 20, thickness: 3, offset: 5 }

/** Le glyphe d'un onglet : la grande taille du livre (16 · 20 · 24). */
const GLYPH_SIZE = 24

/** Au-delà, le compte ne se lit plus dans une chip. */
const MAX_BADGE_COUNT = 99

type PressableVisualState = { pressed?: boolean; focused?: boolean }

export function TabBarButton(props: TabBarButtonProps) {
    const {
        iconName,
        label,
        active,
        onPress,
        activeColor = "role",
        inactiveColor = "text-muted",
        badgeCount = 0,
        href,
        testID,
    } = props
    const webHref = Platform.OS === "web" ? href : undefined

    // L'icône et le libellé de l'onglet ouvert s'ÉCRIVENT : ils prennent l'encre de la couleur, pas
    // son aplat — le corail d'univers de l'élève ne porte pas un libellé de 11 px à l'AA. Le trait,
    // lui, est un aplat.
    const color = active ? textInkOnFill(activeColor) : inactiveColor
    const hasBadge = badgeCount > 0

    return (
        <Pressable
            testID={testID}
            // `href` n'est pas dans les props natives de `Pressable` : react-native-web en fait un <a>.
            {...((webHref != null ? { href: webHref } : {}) as object)}
            onPress={(event) => pressLink(event, webHref, onPress)}
            accessibilityRole="tab"
            accessibilityLabel={hasBadge ? `${label} (${badgeCount})` : label}
            accessibilityState={{ selected: active }}
            style={({ pressed }: PressableVisualState) => [styles.cell, focusRingHost, pressed && styles.pressed]}
        >
            {({ focused }: PressableVisualState) => (
                <>
                    <FocusRing visible={focused === true} radius={12} />
                    <View style={styles.glyph}>
                        <Icon name={iconName} size={GLYPH_SIZE} color={color} />
                        {active && <View style={styles.underline(activeColor)} />}
                        {hasBadge && (
                            <View style={styles.badge} pointerEvents="none">
                                <Badge text={cappedCount(badgeCount, MAX_BADGE_COUNT)} variant="danger" />
                            </View>
                        )}
                    </View>
                    <Text size="text-xs" weight={active ? "bold" : "medium"} color={color} numberOfLines={1}>
                        {label}
                    </Text>
                </>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    cell: {
        minWidth: MIN_TARGET,
        minHeight: MIN_TARGET,
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing["3"],
        paddingHorizontal: theme.spacing["2"],
        paddingVertical: theme.spacing["2"],
        borderRadius: theme.radius.md,
    },
    pressed: { opacity: 0.7 },
    /** Le trait se pose SOUS l'icône : le libellé garde sa place, l'onglet ne saute pas. */
    glyph: { alignItems: "center" },
    underline: (color: SemanticColorKey) => ({
        position: "absolute" as const,
        bottom: -UNDERLINE.offset,
        width: UNDERLINE.width,
        height: UNDERLINE.thickness,
        borderRadius: UNDERLINE.thickness,
        backgroundColor: theme.colors[color],
    }),
    /**
     * La chip mord le coin haut-droit du glyphe et déborde, sans déplacer l'icône : elle en laisse
     * les deux tiers lisibles, à un comme à trois caractères.
     */
    badge: {
        position: "absolute",
        left: (GLYPH_SIZE * 2) / 3,
        bottom: GLYPH_SIZE / 2,
    },
}))
